import { useState } from 'react'
import { useTranslation } from '../i18n'
import { useApi, apiPost } from '../hooks/useApi'
import type { StaleTest, DeleteTestsResult } from '../types'

const RUN_WINDOW_OPTIONS = [1, 2, 3, 5, 10] as const

export function StaleTests() {
  const { t } = useTranslation()
  const [runs, setRuns] = useState(1)
  const stale = useApi<StaleTest[]>(`/tests/stale?runs=${runs}`)

  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set())
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tests = stale.data ?? []

  const toggleTest = (testId: string) => {
    setSelectedTests((prev) => {
      const next = new Set(prev)
      if (next.has(testId)) {
        next.delete(testId)
      } else {
        next.add(testId)
      }
      return next
    })
  }

  const toggleAll = () => {
    setSelectedTests((prev) =>
      prev.size === tests.length ? new Set() : new Set(tests.map((test) => test.id))
    )
  }

  const handleDelete = async () => {
    if (selectedTests.size === 0) {
      return
    }
    if (!window.confirm(t('confirm.deleteStale', { count: selectedTests.size }))) {
      return
    }

    setDeleting(true)
    setError(null)

    try {
      const testIds = Array.from(selectedTests).map((id) => {
        const test = tests.find((candidate) => candidate.id === id)
        if (!test) {
          throw new Error(t('error.failedToFetchTests'))
        }
        return { className: test.className, methodName: test.methodName }
      })

      await apiPost<DeleteTestsResult>('/tests/bulk-delete', { testIds })

      setSelectedTests(new Set())
      stale.refetch()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToFetchTests'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="card" data-testid="stale-tests" style={{ marginTop: '1.5rem' }}>
      <h2 className="card-title">{t('label.staleTests')}</h2>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
        <label htmlFor="stale-threshold">{t('label.staleThreshold')}</label>
        <select
          id="stale-threshold"
          value={runs}
          onChange={(e) => {
            setRuns(Number(e.target.value))
            setSelectedTests(new Set())
          }}
          data-testid="stale-threshold-select"
          className="input"
          style={{ width: 'auto' }}
        >
          {RUN_WINDOW_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {stale.loading ? (
        <div
          style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}
          data-testid="stale-loading"
        >
          {t('label.loading')}
        </div>
      ) : tests.length === 0 ? (
        <div
          style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}
          data-testid="no-stale-tests"
        >
          {t('empty.noStaleTests')}
        </div>
      ) : (
        <>
          <div
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}
          >
            <button
              onClick={toggleAll}
              className="btn btn-secondary btn-sm"
              data-testid="stale-select-all"
            >
              {selectedTests.size === tests.length ? t('button.selectNone') : t('button.selectAll')}
            </button>
            <button
              onClick={handleDelete}
              className="btn btn-danger"
              disabled={selectedTests.size === 0 || deleting}
              data-testid="stale-delete"
              aria-label={t('button.delete')}
              title={t('button.delete')}
            >
              <i className="pi pi-trash" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
              {deleting ? t('misc.deleting') : `${t('button.delete')} (${selectedTests.size})`}
            </button>
          </div>

          {error && (
            <div
              style={{ marginBottom: '1rem', color: 'var(--color-error)', fontSize: '0.875rem' }}
              data-testid="stale-error"
            >
              {error}
            </div>
          )}

          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="stale-list">
              {tests.map((test) => (
                <li
                  key={test.id}
                  style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--color-border)' }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTests.has(test.id)}
                      onChange={() => toggleTest(test.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', flex: 1 }}>
                      <span>{test.className}</span>
                      <span style={{ color: 'var(--color-gray-400)' }}>.{test.methodName}</span>
                    </span>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>
                      {t('label.lastSeenBuildsAgo', { count: test.lastSeenBuildsAgo })}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}
